import { ApolloError } from '@apollo/client';
import { MockList } from '@graphql-tools/mock';

import { mocks } from 'src/modules/api-client/mocks/spec/';

import availabilityOptionsData from '../spec/AvailabilityOptions';
import preferredClassificationOptionsData from '../spec/PreferredClassificationOptions';
import rightToWorkOptionsData from '../spec/RightToWorkOptions';
import salaryOptions from '../spec/SalaryOptions';
import supportedCountries from '../spec/SupportedCountries';
import workTypeOptionsData from '../spec/WorkTypeOptions';

import {
  shareableProfileSummary,
  shareableProfileLanguageProficiencies,
  shareableProfileRoles,
  shareableProfileQualifications,
  shareableProfilePersonalDetails,
  shareableProfileActive,
  shareableProfileLicences,
  shareableProfileVerifiedIdentity,
} from './shareableProfileTestData';
import {
  salaryPreferences,
  rightsToWork as rightsToWorkData,
  rightToWorkVerified,
  personalDetails as personalDetailsData,
  emptyPersonalDetails,
  currentLocation2 as currentLocation2Data,
  licences as licenceData,
  personalStatement,
  profileVisibility as profileVisibilityData,
  workTypes as workTypesData,
  availability as availabilityData,
  preferredClassification as preferredClassificationData,
  preferredLocations2 as preferredLocations2Data,
  skills as skillsData,
  oneOfEachResumeStatus,
  noDefaultResume,
  tenResumes,
  languageProficiencies as languageProficienciesData,
  yearsOfExperience as yearsOfExperienceData,
  personalDetailsNoCountryCallingCode,
  personalDetailsNoPhoneNumber,
  approachability as approachabilityData,
  _id,
  parsedRole,
  parsedQualification,
  scoreNoTips,
  scoreMultipleTips,
  scoreSingleTip,
  deprecatedHomeLocation,
  preferredDeprecatedLocations2,
  deprecatedRightsToWork,
  profileInsightsWithActivities,
  profileInsightsWithoutSearchImpressions,
  profileSuggestionsWithData,
  profileSuggestionsWithoutData,
  profileSuggestionsWithSingleData,
  allRightsToWork,
  withSPIResume,
  verifiedLicencesWithExpiryStatus,
} from './test-data';
import { getStateTesters, handleQueryError } from './utils';

const { returnState, returnEmptyState } = getStateTesters();

export const MutationResult = {
  ref: '1234',
};

const approachability = () => {
  handleQueryError('approachability');

  return approachabilityData;
};

const availabilityOptions = () => {
  handleQueryError('availabilityOptions');

  if (returnEmptyState('availabilityOptions')) {
    return [];
  }

  // eslint-disable-next-line new-cap
  return availabilityOptionsData();
};

const workTypeOptions = () => {
  handleQueryError('workTypeOptions');

  if (returnEmptyState('workTypeOptions')) {
    return [];
  }

  // eslint-disable-next-line new-cap
  return workTypeOptionsData();
};

const salaryPreferences2 = () => {
  handleQueryError('salaryPreferences');

  if (returnEmptyState('salaryPreferences')) {
    return [salaryPreferences[0]];
  }

  return salaryPreferences;
};

const rightsToWork = () => {
  handleQueryError('rightsToWork');

  if (returnEmptyState('rightsToWork')) {
    return [rightsToWorkData[0]];
  }

  const verifiedState = returnState('rightsToWork', 'verified');
  if (verifiedState) {
    return rightToWorkVerified;
  }

  if (returnState('rightsToWork', 'deprecated')) {
    return deprecatedRightsToWork;
  }
  if (returnState('rightsToWork', 'all')) {
    return allRightsToWork;
  }

  return rightsToWorkData;
};

const rightToWorkOptions = () => {
  handleQueryError('rightToWorkOptions');

  if (returnEmptyState('rightToWorkOptions')) {
    return [];
  }

  // eslint-disable-next-line new-cap
  return rightToWorkOptionsData();
};

const licences = () => {
  handleQueryError('licences');

  if (returnEmptyState('licences')) {
    return [];
  }

  if (returnState('licences', 'withExpiryStatus')) {
    return verifiedLicencesWithExpiryStatus;
  }

  return licenceData;
};

const personalDetails = () => {
  handleQueryError('personalDetails');

  if (returnEmptyState('personalDetails')) {
    return emptyPersonalDetails;
  }

  if (returnState('personalDetails', 'noCountryCallingCode')) {
    return personalDetailsNoCountryCallingCode;
  }

  if (returnState('personalDetails', 'noPhoneNumber')) {
    return personalDetailsNoPhoneNumber;
  }

  return personalDetailsData;
};

const currentLocation2 = () => {
  handleQueryError('currentLocation2');

  if (returnEmptyState('currentLocation2')) {
    return null;
  }

  if (returnState('homeLocation', 'deprecated')) {
    return deprecatedHomeLocation;
  }

  return currentLocation2Data;
};

const careerObjectives = () => {
  handleQueryError('careerObjectives');

  if (returnEmptyState('careerObjectives')) {
    return null;
  }

  return personalStatement;
};

const profileVisibility = () => {
  handleQueryError('profileVisibility');

  if (returnEmptyState('profileVisibility')) {
    return null;
  }

  if (returnState('profileVisibility', 'Standard')) {
    return { level: 'Standard' };
  }

  if (returnState('profileVisibility', 'Limited')) {
    return { level: 'Limited' };
  }

  if (returnState('profileVisibility', 'Hidden')) {
    return { level: 'Hidden' };
  }

  return profileVisibilityData;
};

const workTypes = () => {
  handleQueryError('workTypes');

  if (returnEmptyState('workTypes')) {
    return [];
  }

  return workTypesData;
};

const availability = () => {
  handleQueryError('availability');

  if (returnEmptyState('availability')) {
    return null;
  }

  return availabilityData;
};

const preferredClassification = () => {
  handleQueryError('preferredClassification');

  if (returnEmptyState('preferredClassification')) {
    return null;
  }

  return preferredClassificationData;
};

const preferredClassificationOptions = () => {
  handleQueryError('preferredClassificationOptions');

  if (returnEmptyState('preferredClassificationOptions')) {
    return null;
  }

  // eslint-disable-next-line new-cap
  return preferredClassificationOptionsData();
};

const preferredLocations2 = () => {
  handleQueryError('preferredLocations');

  if (returnEmptyState('preferredLocations')) {
    return [];
  }
  if (returnState('preferredLocation', 'deprecated')) {
    return preferredDeprecatedLocations2;
  }

  return preferredLocations2Data;
};

const skills = () => {
  handleQueryError('skills');

  if (returnEmptyState('skills')) {
    return [];
  }

  return skillsData;
};

const resumes = (options: { skipQueryError?: boolean } = {}) => {
  if (!options?.skipQueryError) {
    handleQueryError('resumes');
  }

  if (returnEmptyState('resumes')) {
    return [];
  }

  if (returnState('resumes', 'resumeLimit')) {
    return tenResumes;
  }

  if (returnState('resumes', 'noDefaultResume')) {
    return noDefaultResume;
  }
  if (returnState('resumes', 'withSPIResume')) {
    return withSPIResume;
  }

  return oneOfEachResumeStatus;
};

const resume = () => oneOfEachResumeStatus[4];

const languageProficiencies = () => {
  handleQueryError('languages');

  if (returnEmptyState('languages')) {
    return [];
  }

  return languageProficienciesData;
};

const yearsOfExperience = () => {
  handleQueryError('yearsOfExperience');

  if (returnEmptyState('yearsOfExperience')) {
    return {
      option: null,
      newToWorkforce: false,
    };
  }

  return yearsOfExperienceData;
};

const score = () => {
  handleQueryError('score');

  if (returnEmptyState('score')) {
    return scoreNoTips;
  }

  if (returnState('score', 'single')) {
    return scoreSingleTip;
  }

  if (returnState('score', 'multiple')) {
    return scoreMultipleTips;
  }
};

const profileSuggestions = () => {
  handleQueryError('suggestions');

  if (returnEmptyState('suggestions')) {
    return profileSuggestionsWithoutData;
  }

  if (returnState('suggestions', 'single')) {
    return profileSuggestionsWithSingleData;
  }

  if (returnState('suggestions', 'multiple')) {
    return profileSuggestionsWithData;
  }
};

const unconfirmedDataForContext = () => {
  if (returnState('unconfirmedData', 'found')) {
    return {
      __typename: 'UnconfirmedDataCompleted',
      completed: true,
      roles: [parsedRole],
      qualifications: [parsedQualification],
    };
  }
  return {
    __typename: 'UnconfirmedDataPending',
    completed: false,
  };
};

const shareableProfileSettings = () => {
  handleQueryError('shareableProfileSettings');

  if (returnEmptyState('shareableProfileSettings')) {
    return {
      profileSlug: null,
      active: null,
      views: null,
      url: null,
      displayUrl: null,
    };
  }

  const defaultSettings = {
    profileSlug: 'fred-smith-sJT3vHkW7h',
    active: true,
    views: 0,
    url: 'https://www.seek.com.au/profile/fred-smith-sJT3vHkW7h',
    displayUrl: 'seek.com.au/profile/fred-smith-sJT3vHkW7h',
  };

  if (returnState('shareableProfileSettings', 'inactive')) {
    return {
      ...defaultSettings,
      active: false,
    };
  }

  return defaultSettings;
};

const profileInsights = () => {
  handleQueryError('profileInsights');

  if (returnState('profileInsights', 'noSearchImpressions')) {
    return profileInsightsWithoutSearchImpressions;
  }

  if (returnEmptyState('profileInsights')) {
    return null;
  }

  return profileInsightsWithActivities;
};

const Candidate = () => {
  if (returnState('page', 'auth-error')) {
    const authError = new Error('Auth request failed');
    // @ts-expect-error
    authError.extensions = { code: 'UNAUTHENTICATED' };
    throw authError;
  }

  return {
    // eslint-disable-next-line new-cap
    ...mocks.Candidate(),
    _id,
    approachability,
    emailAddress: 'henry_perez@gmail.net',
    salaryPreferences2,
    rightsToWork,
    licences,
    personalDetails,
    currentLocation2,
    careerObjectives,
    profileVisibility,
    workTypes,
    availability,
    preferredClassification,
    preferredLocations2,
    skills,
    resumes,
    resume,
    languageProficiencies,
    yearsOfExperience,
    score,
    profileSuggestions,
    unconfirmedDataForContext,
    shareableProfileSettings,
    profileInsights,
  };
};

const shareableProfile = () => {
  if (returnEmptyState('shareableProfile')) {
    return {
      id: 'abc123',
      active: true,
      personalDetails: {
        firstName: null,
        lastName: null,
        __typename: 'PersonalDetails',
      },
      summary: null,
      roles: [],
      qualifications: [],
      licences: [],
      skills: [],
      languageProficiencies: [],
      verifiedIdentity: null,
      __typename: 'ShareableProfile',
    };
  }

  return {
    active: shareableProfileActive(),
    // Only need custom mocks for these ones as they resolve to field names rather than types
    personalDetails: shareableProfilePersonalDetails(),
    summary: shareableProfileSummary(),
    languageProficiencies: shareableProfileLanguageProficiencies(),
    // Needed custom ones for shareable profile as the empty state is different
    roles: shareableProfileRoles(),
    qualifications: shareableProfileQualifications(),
    licences: shareableProfileLicences(),
    verifiedIdentity: shareableProfileVerifiedIdentity(),
  };
};

const createGraphQLError = (message: string, code: string) =>
  new ApolloError({
    graphQLErrors: [
      {
        message,
        path: ['viewer'],
        extensions: { code },
      },
    ],
    networkError: null,
  });

export const mockOverrides = {
  ...mocks,
  MutationResult,
  Candidate,
  resumes,
  Query: () => ({
    viewer: () => {
      if (returnState('page', 'load-error')) {
        throw createGraphQLError(
          'Failed to load profile',
          'INTERNAL_SERVER_ERROR',
        );
      }
    },
    // eslint-disable-next-line new-cap
    ontologyEducationProviders: () => new MockList([10, 10]),
    ontologyOrganisations: () => new MockList([10, 10]),
    ontologyRoleTitlesSeniority: () => new MockList([10, 10]),
    ontologyQualifications: () => new MockList([10, 10]),
    salaryOptions,
    rightToWorkOptions,
    availabilityOptions,
    workTypeOptions,
    preferredClassificationOptions,
    supportedCountries,
    shareableProfile,
  }),
};
